import React, { FC, useRef, useLayoutEffect, useState } from "react";
import { Box, Card, Typography, Fade } from "@mui/material";
import { SxProps, Theme } from "@mui/system";
import { MatchsPlayerParticipation } from "../../types/MatchsPlayerParticipation";

import { getPlayerCardColors } from "../../utils/boardLiveColor";

type Props = {
  matchPlayers: Array<MatchsPlayerParticipation>;
  boardColor?: string;
};

const AutoScaleText: FC<{ text: string }> = ({ text }) => {
  const ref = useRef<HTMLSpanElement>(null);
  const [scale, setScale] = useState(1);

  useLayoutEffect(() => {
    const element = ref.current;
    if (element) {
      const parentWidth = element.parentElement?.offsetWidth || 1;
      const actualWidth = element.scrollWidth;
      const newScale = Math.min(1, parentWidth / actualWidth);
      setScale(newScale);
    }
  }, [text]);

  return (
    <span
      ref={ref}
      style={{
        display: "inline-block",
        transform: `scaleX(${scale})`,
        transformOrigin: "left",
        whiteSpace: "nowrap",
      }}
    >
      {text}
    </span>
  );
};

const TablePlayers: FC<Props> = ({ matchPlayers, boardColor }) => {
  const getPosition = (index: number, total: number) => {
    return index === 0
      ? { top: -26 }
      : index === 2
      ? { top: 36 }
      : index === 3
      ? { bottom: 36 }
      : { bottom: -26 };
  };

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height: "100%",
      }}
    >
      {matchPlayers.map((mp, index) => {
        const { backgroundColor, borderColor, textColor } = getPlayerCardColors(
          boardColor,
          index
        );
        const cardStyle: SxProps<Theme> = {
          backgroundColor,
          border: `2px solid ${borderColor}`,
          position: "absolute",
          px: 1.5,
          py: 0.75,
          width: 140,
          borderRadius: 2,
          boxShadow: 3,
          left: "50%",
          transform: "translateX(-50%) !important",
          textAlign: "center",
          ...(getPosition(index, matchPlayers.length) as Record<string, any>),
        };

        return (
          <Fade in timeout={500} key={mp.id}>
            <Card sx={cardStyle}>
              <Typography
                variant="body2"
                fontWeight="bold"
                noWrap
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  fontSize: 16,
                  color: textColor,
                }}
              >
                <AutoScaleText text={mp.players?.name || ""} />
              </Typography>
              <Typography
                variant="body2"
                noWrap
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  fontSize: 15,
                  color: textColor,
                }}
              >
                <AutoScaleText text={mp.players?.surname || ""} />
              </Typography>
            </Card>
          </Fade>
        );
      })}
    </Box>
  );
};

export default TablePlayers;
